import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {RequestToken} from "../models/request-token";
import {BehaviorSubject, Observable, of, tap} from "rxjs";
import {PayloadAddEditUserExtern, UserExternProps} from '../models/customer-extern';
import {catchError, map} from 'rxjs/operators';
import {Filter} from "./demanda.service";
import {PageProps} from "../models/pagination";

@Injectable({
  providedIn: 'root'
})
export class UsuarioExternoService {

  private readonly apiUri = environment.api.uri.concat('usuario-externo');
  private readonly usuarioLogado$ = new BehaviorSubject<UserExternProps | null>(null);

  constructor(private readonly http: HttpClient) {
  }

  requestToken(email: string): Observable<any> {
    const url = `${this.apiUri}/request-token`;
    return this.http.post<any>(url, {email});
  }

  validateToken(email: string, token: string) {
    const url = `${this.apiUri}/validate-token`;
    return this.http.post<RequestToken>(url, {email, token});
  }

  getUsuarioLogado(): Observable<UserExternProps> {
    return this.http.get<UserExternProps>(`${this.apiUri}/obter-usuario-externo`, {}).pipe(
      tap(usuario => this.usuarioLogado$.next(usuario))
    );
  }

  get usuario(): UserExternProps | null {
    return this.usuarioLogado$.value;
  }

  get usuario$(): Observable<UserExternProps | null> {
    return this.usuarioLogado$.asObservable();
  }

  isAdministrador(): Observable<boolean> {
    if (this.usuarioLogado$.value) {
      return of(!!this.usuarioLogado$.value.administrador);
    }
    return this.getUsuarioLogado().pipe(
      map(usuario => !!usuario?.administrador),
      catchError(() => of(false))
    );
  }

  getUsuariosExternos(
    page: number,
    size: number,
    sortField: string | null | undefined,
    sortDirection: string | null | undefined,
    filters: Filter[]
  ): Observable<PageProps<UserExternProps>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (sortField && (sortDirection === 'asc' || sortDirection === 'desc')) {
      params = params.append('sort', `${sortField},${sortDirection}`);
    }

    for (const filtro of filters) {
      if (filtro.value !== null && filtro.value !== undefined && String(filtro.value).trim() !== '') {
        params = params.set(filtro.field, String(filtro.value));
      }
    }

    params = params.append('isConcessionaria', true);

    return this.http.get<PageProps<UserExternProps>>(this.apiUri, {params});
  }

  createUsuarioExterno(usuario: PayloadAddEditUserExtern): Observable<any> {
    return this.http.post<any>(this.apiUri, usuario);
  }

  editUsuarioExterno(id: number, payload: PayloadAddEditUserExtern): Observable<any> {
    const params = new HttpParams().set('id', id.toString());
    return this.http.put<any>(this.apiUri, payload, {params});
  }

  getUsuarioExternoById(id: number): Observable<UserExternProps> {
    return this.http.get<UserExternProps>(`${this.apiUri}/${id}`);
  }

  ativarDesativarUsuario(id: number, ativo: boolean): Observable<string> {
    const params = new HttpParams()
      .append('id', id)
      .append('ativo', ativo.toString());
    return this.http.put<string>(`${this.apiUri}/ativar-desativar`, null, {params, responseType: 'text' as 'json'});
  }

  clearUsuario(): void {
    this.usuarioLogado$.next(null);
  }
}
